import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router";
import { api } from "../../data";
import { Dialog } from "../../components/ui";
import { useInsightsBereit } from "../../components/Tarif";
import { tr, type Lang } from "./sprache";

/**
 * Erinnerung „Verbinde dein Instagram“ (Entscheidung des Inhabers vom 16.10.2026, Vorbild ttIgLoginHinweis im Altportal):
 * einmal je Anmeldung, nur für Creator ohne Verbindung und nur, wenn die Agentur das Verbinden erlaubt hat; schließbar, kommt
 * beim Seitenwechsel nicht wieder. „Je Anmeldung“ hängt am Zeitpunkt der Anmeldung (Session.signedInAt): ein neuer Tab derselben
 * Anmeldung fragt nicht erneut, die nächste Anmeldung schon. Ohne diesen Zeitpunkt gilt die Sitzung dieses Tabs.
 * Solange Instagram-Insights auf der Plattform in Vorbereitung sind (Meta-Prüfung), drängt sie niemanden zum Verbinden.
 */
const KEY = "tk-ig-erinnerung";

export function erinnerungSchluessel(userId: string, talentId: string): string {
  return `${KEY}:${userId}:${talentId}`;
}
/** Schon gezeigt für diese Anmeldung? (Speicher gesperrt: als gezeigt werten, lieber gar nicht als bei jedem Seitenwechsel) */
export function schonGezeigt(userId: string, talentId: string, signedInAt: string | null | undefined): boolean {
  try {
    const key = erinnerungSchluessel(userId, talentId);
    return signedInAt ? localStorage.getItem(key) === signedInAt : sessionStorage.getItem(key) === "1";
  } catch {
    return true;
  }
}
export function alsGezeigtMerken(userId: string, talentId: string, signedInAt: string | null | undefined): void {
  try {
    const key = erinnerungSchluessel(userId, talentId);
    if (signedInAt) localStorage.setItem(key, signedInAt);
    else sessionStorage.setItem(key, "1");
  } catch {
    /* Speicher gesperrt: nichts zu merken */
  }
}

export function InstagramErinnerung({ userId, talentId, orgId, igEnabled, archived, lang }: {
  userId: string; talentId: string; orgId: string; igEnabled: boolean; archived: boolean; lang: Lang;
}) {
  const t = (de: string, en: string) => tr(lang, de, en);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const bereit = useInsightsBereit();
  const moeglich = api.mode === "demo" || (bereit && !!api.instagramConnect);
  const signedInAt = api.session()?.signedInAt ?? null;
  const [offen, setOffen] = useState(false);
  const pruefen = igEnabled && !archived && moeglich && !schonGezeigt(userId, talentId, signedInAt);
  const status = useQuery({
    queryKey: ["creator", talentId, "instagram", "status"],
    enabled: pruefen,
    queryFn: async () => (await api.select<{ status: string }>("ig_accounts", { columns: "status", filters: [["talent_id", "eq", talentId], ["org_id", "eq", orgId]] }))[0]?.status ?? null,
  });
  useEffect(() => {
    if (!pruefen || status.isPending || status.error || status.data !== null) return;
    // Auf der Instagram-Seite selbst ist der Knopf schon da: dort nicht fragen und nicht als gezeigt werten
    if (pathname.startsWith("/creator/instagram")) return;
    alsGezeigtMerken(userId, talentId, signedInAt);
    setOffen(true);
  }, [pruefen, status.isPending, status.error, status.data, pathname, userId, talentId, signedInAt]);

  return (
    <Dialog open={offen} onClose={() => setOffen(false)} title={t("Verbinde dein Instagram", "Connect your Instagram")}>
      <p>{t("Deine Agentur hat das Verbinden für dein Profil freigeschaltet. Mit der Verbindung siehst du deine Insights hier im Portal, und deine Agentur kann Marken auf Wunsch aktuelle Zahlen zeigen, nur mit deiner Zustimmung.",
        "Your agency has enabled connecting for your profile. Once connected you see your insights here in the portal, and your agency can show brands current figures on request, only with your consent.")}</p>
      <p>{t("Du meldest dich direkt bei Instagram an. CreatorOffice fragt nie nach deinem Passwort und liest keine Nachrichten.",
        "You sign in directly with Instagram. CreatorOffice never asks for your password and does not read messages.")}</p>
      <div className="row">
        <button className="primary" onClick={() => { setOffen(false); navigate("/creator/instagram"); }}>{t("Jetzt verbinden", "Connect now")}</button>
        <button onClick={() => setOffen(false)}>{t("Später", "Later")}</button>
      </div>
    </Dialog>
  );
}
